import { useState } from 'react';
import { useWallet, useConnection } from '@solana/wallet-adapter-react';
import { VersionedTransaction } from '@solana/web3.js';
import { TOKEN_MINT } from '../config/constants';
import { isMobileDevice } from '../utils/walletLinks';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

interface TradeInterfaceProps {
  publicKey: string | null;
}

export function TradeInterface({ publicKey }: TradeInterfaceProps) {
  const { signTransaction, sendTransaction, publicKey: walletPublicKey, wallet } = useWallet();
  const { connection } = useConnection();
  const [action, setAction] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [slippage, setSlippage] = useState('1');
  const [priorityFee, setPriorityFee] = useState('0.00001');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMobile = isMobileDevice();

  // Use wallet public key if provided, otherwise fallback to prop
  const activePublicKey = walletPublicKey?.toString() || publicKey;

  const handleTrade = async () => {
    if (!activePublicKey) {
      setError('Please connect your wallet first');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/trade-local`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          publicKey: activePublicKey,
          action,
          mint: TOKEN_MINT,
          amount: parseFloat(amount),
          denominatedInSol: action === 'buy' ? 'true' : 'false',
          slippage: parseFloat(slippage),
          priorityFee: parseFloat(priorityFee),
        }),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Failed to create transaction');
      }

      // Get transaction as ArrayBuffer
      const transactionBuffer = await response.arrayBuffer();
      const transactionBase64 = btoa(
        String.fromCharCode(...new Uint8Array(transactionBuffer))
      );

      // Sign and send transaction
      await signAndSendTransaction(transactionBase64);
    } catch (err: any) {
      setError(err.message || 'Failed to create transaction');
    } finally {
      setLoading(false);
    }
  };

  const signAndSendTransaction = async (transactionBase64: string) => {
    if (!signTransaction || !sendTransaction) {
      setError('Wallet not connected. Please connect your wallet first.');
      return;
    }

    try {
      console.log('Starting transaction on', isMobile ? 'mobile' : 'desktop', 'with wallet:', wallet?.adapter.name);
      
      // Deserialize transaction
      const transactionBuffer = Uint8Array.from(atob(transactionBase64), c => c.charCodeAt(0));
      
      // Check transaction size (Solana has a 1232 byte limit)
      if (transactionBuffer.length > 1232) {
        throw new Error(`Transaction too large: ${transactionBuffer.length} bytes (max 1232). This may cause issues on mobile wallets.`);
      }
      
      console.log('Transaction deserialized, size:', transactionBuffer.length, 'bytes');
      const transaction = VersionedTransaction.deserialize(transactionBuffer);
      
      // Sign transaction with wallet adapter
      console.log('Requesting signature from wallet...');
      const signedTransaction = await signTransaction(transaction);
      console.log('Transaction signed successfully');

      // Mobile wallets (especially WalletConnect) may have issues with preflight
      // Skip preflight on mobile to avoid timeout issues, but keep it on desktop for safety
      const sendOptions = {
        skipPreflight: isMobile, // Skip preflight on mobile to avoid timeouts
        maxRetries: isMobile ? 5 : 3, // More retries on mobile
        preflightCommitment: 'processed' as const, // Use processed for faster confirmation
      };
      
      console.log('Sending transaction with options:', sendOptions);
      
      // Send transaction using wallet adapter
      const signature = await sendTransaction(signedTransaction, sendOptions);
      console.log('Transaction sent, signature:', signature);

      // On mobile, don't wait for full confirmation to avoid timeouts
      // The wallet app will handle confirmation
      if (isMobile) {
        // For mobile, just show success message immediately
        // The wallet app will show the transaction status
        alert(`Transaction submitted! Signature: ${signature.slice(0, 8)}...\n\nView on Solscan: https://solscan.io/tx/${signature}`);
        setAmount('');
        
        // Optionally check status in background (don't block UI)
        setTimeout(async () => {
          try {
            const status = await connection.getSignatureStatus(signature);
            console.log('Transaction status:', status);
            if (status.value?.err) {
              console.error('Transaction failed:', status.value.err);
            }
          } catch (err) {
            console.error('Error checking transaction status:', err);
          }
        }, 2000);
      } else {
        // On desktop, wait for confirmation
        console.log('Waiting for transaction confirmation...');
        await connection.confirmTransaction(signature, 'confirmed');
        console.log('Transaction confirmed');
        alert(`Transaction successful! View on Solscan: https://solscan.io/tx/${signature}`);
        setAmount('');
      }
    } catch (err: any) {
      console.error('Transaction error:', err);
      console.error('Error details:', {
        name: err.name,
        message: err.message,
        code: err.code,
        stack: err.stack,
        wallet: wallet?.adapter.name,
        isMobile,
      });
      
      // Provide more detailed error messages
      let errorMessage = 'Failed to sign or send transaction';
      
      if (err.message) {
        errorMessage = err.message;
      } else if (err.code) {
        // Solana error codes
        switch (err.code) {
          case 4001:
            errorMessage = 'Transaction rejected by user';
            break;
          case -32603:
            errorMessage = 'Transaction simulation failed. Check your balance and try again.';
            break;
          default:
            errorMessage = `Transaction failed: ${err.code}`;
        }
      }
      
      // Check for specific error patterns
      if (err.message?.includes('insufficient funds') || err.message?.includes('insufficient balance')) {
        errorMessage = 'Insufficient SOL balance. Please add more SOL to your wallet.';
      } else if (err.message?.includes('slippage')) {
        errorMessage = 'Slippage tolerance exceeded. Try increasing slippage or reducing amount.';
      } else if (err.message?.includes('simulation')) {
        errorMessage = 'Transaction simulation failed. The transaction may be invalid.';
      } else if (err.message?.includes('user rejected') || err.message?.includes('User rejected')) {
        errorMessage = 'Transaction was rejected. Please try again.';
      } else if (err.message?.includes('timeout') || err.message?.includes('Timeout')) {
        errorMessage = 'Transaction timed out. Please try again with a higher priority fee.';
      } else if (err.message?.includes('network') || err.message?.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      // Mobile-specific error messages
      if (isMobile) {
        if (err.message?.includes('WalletConnect') || wallet?.adapter.name === 'WalletConnect') {
          errorMessage += ' (Mobile wallet issue - try disconnecting and reconnecting your wallet)';
        }
      }
      
      setError(errorMessage);
    }
  };

  return (
    <div className="trade-interface">
      <div className="trade-tabs">
        <button
          className={action === 'buy' ? 'active' : ''}
          onClick={() => setAction('buy')}
        >
          Buy
        </button>
        <button
          className={action === 'sell' ? 'active' : ''}
          onClick={() => setAction('sell')}
        >
          Sell
        </button>
      </div>

      <div className="trade-form">
        <div className="form-group">
          <label>Amount ({action === 'buy' ? 'SOL' : 'Tokens'})</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.0"
            step="0.000001"
          />
        </div>

        <div className="form-group">
          <label>Slippage (%)</label>
          <input
            type="number"
            value={slippage}
            onChange={(e) => setSlippage(e.target.value)}
            placeholder="1"
            step="0.1"
            min="0"
            max="100"
          />
        </div>

        <div className="form-group">
          <label>Priority Fee (SOL)</label>
          <input
            type="number"
            value={priorityFee}
            onChange={(e) => setPriorityFee(e.target.value)}
            placeholder="0.00001"
            step="0.000001"
            min="0"
          />
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          onClick={handleTrade}
          disabled={loading || !activePublicKey}
          className="trade-button"
        >
          {loading ? 'Processing...' : `${action === 'buy' ? 'Buy' : 'Sell'} Tokens`}
        </button>
      </div>
    </div>
  );
}

